// frontend/src/modules/reports/pages/ReportsPage.tsx
//
// Phase 4 (Reports Frontend), Task 1. Reports Center: Workforce | Financial
// tabs, each rendering its family's registry (workforce/registry.ts,
// financial/registry.ts — see reports/registry.ts for the ownership seam).
//
// Visibility (fail closed to hidden, mirrors the plan's visibility matrix):
//   - The whole page requires `reports.view` (the nav gate) — otherwise
//     NoAccessState, full stop.
//   - A tab is shown iff at least one of its family's reports is visible to
//     this user (module(s) enabled + permission key(s) held).
//   - Each report card is individually gated the same way — a user with
//     `reports.view` but none of the workforce keys sees an empty (or
//     hidden) Workforce tab, never a report they'd 403 on.
// A 403 that slips through (e.g. a module disabled mid-session) is handled
// by the report page itself via ReportShell's `access` prop, not here.

import * as React from 'react';
import { BarChart3, Clock3 } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { PageSkeleton } from '../../../design-system/components/PageSkeleton';
import { EmptyState } from '../../../design-system/components/EmptyState';
import { NoAccessState } from '../../../design-system/components/NoAccessState';
import { VISUAL } from '../../../design-system/visual';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs';
import { usePermission, usePermissions } from '../../../core/auth/usePermission';
import { useEnabledModules } from '../../../core/modules/EnabledModulesProvider';
import { isReportVisible, type ReportRegistryEntry } from '../registry';
import { workforceReportsRegistry } from './workforce/registry';
import { financialReportsRegistry } from './financial/registry';

type FamilyKey = 'workforce' | 'financial';

function ReportGrid({
  entries, selectedKey, onSelect,
}: { entries: ReportRegistryEntry[]; selectedKey: string | null; onSelect: (key: string) => void }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {entries.map((e) => (
        <button
          key={e.key}
          type="button"
          onClick={() => onSelect(e.key)}
          className={[
            VISUAL.card, 'text-left p-5 transition-all hover:-translate-y-px',
            selectedKey === e.key ? 'ring-2 ring-saffron' : '',
          ].join(' ')}
        >
          <div className="flex items-center gap-2 mb-1.5 text-muted-foreground/70">
            <BarChart3 className="h-4 w-4" />
          </div>
          <h3 className="font-sans font-semibold text-[15px] text-foreground">{e.title}</h3>
          <p className="text-[13px] text-muted-foreground mt-1 leading-relaxed">{e.description}</p>
        </button>
      ))}
    </div>
  );
}

function FamilyTab({
  entries, storageKey,
}: { entries: ReportRegistryEntry[]; storageKey: string }) {
  const [selectedKey, setSelectedKey] = React.useState<string | null>(entries[0]?.key ?? null);
  React.useEffect(() => {
    if (!entries.some((e) => e.key === selectedKey)) setSelectedKey(entries[0]?.key ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries.map((e) => e.key).join(',')]);

  if (entries.length === 0) {
    return (
      <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
        <EmptyState
          icon={<Clock3 />}
          tone="muted"
          title="No reports available yet"
          description="Reports appear here once the relevant module is enabled and this task's build lands."
        />
      </div>
    );
  }

  const selected = entries.find((e) => e.key === selectedKey) ?? entries[0];
  const Report = selected.component;

  return (
    <div className="space-y-6">
      <ReportGrid entries={entries} selectedKey={selected.key} onSelect={setSelectedKey} />
      <div key={storageKey + selected.key}>
        <Report />
      </div>
    </div>
  );
}

export function ReportsPage() {
  const canViewReports = usePermission('reports.view');
  const perms = usePermissions();
  const { modules, loaded } = useEnabledModules();

  if (!loaded) return <PageSkeleton />;

  if (!canViewReports) {
    return (
      <div>
        <PageHeader eyebrow="Reports" title="Reports" />
        <div className="px-6 sm:px-8 lg:px-10 pb-12 max-w-6xl">
          <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <NoAccessState description="Reports are available to roles holding the reports.view permission. Ask a workspace owner if you think you should have access." />
          </div>
        </div>
      </div>
    );
  }

  const workforceVisible = workforceReportsRegistry.filter((e) => isReportVisible(e, modules, perms));
  const financialVisible = financialReportsRegistry.filter((e) => isReportVisible(e, modules, perms));

  const tabs: Array<{ key: FamilyKey; label: string; entries: ReportRegistryEntry[] }> = [
    ...(workforceVisible.length > 0 ? [{ key: 'workforce' as FamilyKey, label: 'Workforce', entries: workforceVisible }] : []),
    ...(financialVisible.length > 0 ? [{ key: 'financial' as FamilyKey, label: 'Financial', entries: financialVisible }] : []),
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Reports"
        title="Reports"
        description="Workforce and financial insight across every module you have enabled."
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 max-w-6xl">
        {tabs.length === 0 ? (
          <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <NoAccessState
              title="No reports available"
              description="You don't hold a permission key for any workforce or financial report yet. Ask a workspace owner if you think you should have access."
            />
          </div>
        ) : (
          <Tabs defaultValue={tabs[0].key}>
            <TabsList>
              {tabs.map((t) => (
                <TabsTrigger key={t.key} value={t.key}>{t.label}</TabsTrigger>
              ))}
            </TabsList>
            {tabs.map((t) => (
              <TabsContent key={t.key} value={t.key} className="pt-6">
                <FamilyTab entries={t.entries} storageKey={t.key} />
              </TabsContent>
            ))}
          </Tabs>
        )}
      </div>
    </div>
  );
}
